import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { stageApi, timeAgo, compact } from "@/lib/stageApi";
import { PostCard, CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { CommentTools, GiftsWall } from "@/pages/stage/Social";

function Comment({ c, depth = 0 }) {
  const a = c.author || {};
  return (
    <li className={depth ? "ml-10" : ""}>
      <div className="flex items-start gap-3">
        <Link to={`/@${a.handle}`}><CreatorAvatar author={a} size={depth ? 28 : 34} /></Link>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-1.5 text-sm">
            <Link to={`/@${a.handle}`} className="font-semibold hover:underline">{a.display_name || a.handle}</Link>
            <VerifiedMark author={a} size={13} />
            <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
          </p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{c.text}</p>
          <CommentTools c={c} depth={depth} Child={Comment} />
        </div>
      </div>
      {Array.isArray(c.replies_preview) && c.replies_preview.length > 0 && (
        <ul className="mt-3 space-y-3">{c.replies_preview.map((r) => <Comment key={r.id} c={r} depth={1} />)}</ul>
      )}
    </li>
  );
}

export default function PostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined);
  const [comments, setComments] = useState(undefined);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const author = post?.author;
  usePageMeta(author ? `${author.display_name || author.handle} on Pulse` : "Post", post?.text ? post.text.slice(0, 150) : "A post on Fable + Stage.");

  useEffect(() => {
    let alive = true;
    setPost(undefined);
    setComments(undefined);
    stageApi.post(id).then((p) => alive && setPost(p)).catch((e) => alive && setPost({ error: e?.status === 404 ? "This post isn't available." : e?.message || "Couldn't load this post." }));
    stageApi.comments(id).then((d) => alive && setComments(d.items || [])).catch(() => alive && setComments([]));
    return () => {
      alive = false;
    };
  }, [id, user?.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { from: `/stage/post/${id}` } });
      return;
    }
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const c = await stageApi.comment(id, t);
      setComments((prev) => [...(prev || []), { ...c, replies_preview: [] }]);
      setText("");
    } catch (err) {
      toast.error(err.message || "Couldn't post that comment.");
    } finally {
      setSending(false);
    }
  };

  if (post === undefined) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="mt-4 h-24 rounded-2xl" />
      </div>
    );
  }
  if (post.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
        <p className="font-semibold">{post.error}</p>
        <Link to="/stage/feed"><Button variant="outline" className="mt-5 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> Back to Pulse</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
      <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/stage/feed"))} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back
      </button>
      <PostCard post={post} />
      <GiftsWall kind="post" refId={id} />
      <section className="mt-6" aria-labelledby="comments-h">
        <h2 id="comments-h" className="font-bold">Comments {Array.isArray(comments) && comments.length > 0 && <span className="font-mono-num text-sm text-muted-foreground">({comments.length})</span>}</h2>
        <form onSubmit={send} className="mt-3 flex items-start gap-3">
          <CreatorAvatar author={user ? { display_name: user.full_name, avatar_url: user.avatar_url } : {}} size={34} />
          <div className="flex-1">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500} placeholder={user ? "Add a comment…" : "Sign in to comment"} onFocus={() => !user && navigate("/login", { state: { from: `/stage/post/${id}` } })} data-testid="comment-input" className="w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <div className="mt-1.5 flex justify-end">
              <Button type="submit" size="sm" className="rounded-full px-5" disabled={sending || !text.trim()}>{sending ? "Posting…" : "Post"}</Button>
            </div>
          </div>
        </form>
        {comments === undefined ? (
          <div className="mt-4 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
        ) : comments.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No comments yet. Be the first.</p>
        ) : (
          <ul className="mt-4 space-y-4" data-testid="comments">{comments.map((c) => <Comment key={c.id} c={c} />)}</ul>
        )}
      </section>
    </div>
  );
}
